/* eslint-disable no-useless-constructor */
import { MouseEvent, useState } from 'react';
import { Box, Stack } from '@mui/material';
import clsx from 'clsx';
import { Link, useLocation } from 'react-router-dom';
import Icon, { ICONS } from 'components/Icon';
import IconButton from 'components/IconButton';
import Menu, { MenuItemProps } from 'components/Menu';
import Tabs, { Tab } from 'components/Tabs';
import Text from 'components/Text';
import { NavLinkWithChip } from 'components/gms/AppHeader';
import { OrgSelector } from 'components/gms/OrgSelector';
import { useAppContext } from 'hooks/useAppContext';
import { OrganizationRole, getRoleInt } from 'hooks/useRenderIfRole';
import { useUser } from 'queries/UseUsers';
import { logoutService } from 'services/auth0Service';
import { resolvedEnvironment } from 'services/environment';
import { IOrganization } from 'services/types/userTypes';
import { joinPath } from 'utils/pathHelpers';
import './AppHeader.scss';

export const AppHeader = (): JSX.Element => {
  const { data: user } = useUser();
  const { pathname: currentPath } = useLocation();
  const { organizationInfo, selectedOrganization, envConfig } = useAppContext();
  const pointOGmsBaseUrl =
    envConfig?.pointOGmsBaseUrl ?? resolvedEnvironment.pointOGmsBaseUrl;
  const dropdownLabel = `Hi, ${user?.firstName}`;

  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [settingsMenuAnchorEl, setSettingsMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userMenuAnchorEl);
  const settingsMenuOpen = Boolean(settingsMenuAnchorEl);
  const handleUserMenuClick = (event: MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };
  const handleSettingsMenuClick = (event: MouseEvent<HTMLElement>) => {
    setSettingsMenuAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setUserMenuAnchorEl(null);
    setSettingsMenuAnchorEl(null);
  };
  const handleLogout = async () => {
    await logoutService();
  };

  const navTabs = [
    {
      label: 'Dashboard',
      to: '/dashboard',
      value: '/dashboard'
    },
    {
      label: 'Fundraising',
      to: '/g/fundraising/campaigns',
      value: '/g/fundraising/campaigns',
      externalTo: '/fundraising'
    },
    {
      label: 'Reporting',
      to: organizationInfo?.features?.reporting ? '/reporting' : '/donations',
      value: '/reporting',
      externalTo: '/reporting'
    },
    {
      label: 'A/B Testing',
      to: '/strategy',
      value: '/strategy',
      isFeatureEnabled: organizationInfo?.features?.abTesting
    }
  ];

  const reportingViews = ['/donations', '/donations/recurring', '/donors'];

  const isReportingTab = (path: string) =>
    reportingViews.includes(path) ? '/reporting' : false;

  const profileMenu: MenuItemProps[] = [
    {
      label: 'My Profile',
      path: 'my-profile',
      ...(pointOGmsBaseUrl && {
        externalHref: joinPath(pointOGmsBaseUrl, 'user-profile')
      })
    },
    { label: 'Logout', path: '/', onClick: handleLogout }
  ];

  // When forwarding to point-o, use externalPath to match point-o AppRoutes (GlobalNavTwoPointO)
  const withExternal = (
    item: MenuItemProps & { externalPath?: string }
  ): MenuItemProps => {
    const { externalPath, ...rest } = item;
    return pointOGmsBaseUrl && (externalPath ?? rest.path)
      ? {
          ...rest,
          externalHref: joinPath(
            pointOGmsBaseUrl,
            externalPath ?? rest.path ?? ''
          )
        }
      : rest;
  };

  const settingsMenu: MenuItemProps[] = [
    withExternal({
      label: 'Organization Settings',
      path: 'settings/general',
      externalPath: 'org-settings',
      onClick: handleClose
    }),
    ...(getRoleInt(selectedOrganization.role) >=
    getRoleInt(OrganizationRole.Editor)
      ? [
          withExternal({
            label: 'Users',
            path: 'users',
            externalPath: 'org-users',
            onClick: handleClose
          })
        ]
      : []),
    withExternal({
      label: 'Payment Gateways',
      path: 'settings/payment-gateways',
      externalPath: 'gateways',
      onClick: handleClose
    }),
    withExternal({
      label: 'Reconciliation',
      path: 'settings/reconciliation',
      externalPath: 'reconciliation',
      onClick: handleClose
    }),
    withExternal({
      label: 'Integrations',
      path: 'settings/integrations',
      externalPath: 'ix-integrations',
      onClick: handleClose
    }),
    ...(organizationInfo?.features?.integrations2025 &&
    getRoleInt(selectedOrganization.role) >= getRoleInt(OrganizationRole.Admin)
      ? [
          withExternal({
            label: 'Integrations (Beta)',
            path: 'integrations',
            externalPath: 'integrations',
            onClick: handleClose
          })
        ]
      : []),
    withExternal({
      label: 'Webhooks',
      path: 'settings/webhooks',
      externalPath: 'webhooks',
      onClick: handleClose
    }),
    withExternal({
      label: 'Text Settings',
      path: 'settings/texting',
      externalPath: 'text-settings',
      onClick: handleClose
    }),
    withExternal({
      label: 'Designated Funds',
      path: 'settings/designated-funds',
      externalPath: 'fundraising/designations',
      onClick: handleClose
    }),
    withExternal({
      label: 'New Offline Donation',
      path: 'donations/offline/new',
      externalPath: 'offline-donation-new'
    }),
    withExternal({
      label: 'Import Donations',
      path: 'donations/offline/import',
      externalPath: 'offline-donation-import'
    })
  ];

  const menuIconClass = clsx('AppHeader-MenuIcon', {
    'AppHeader-MenuIconOpen': userMenuOpen
  });
  const settingsButtonClass = clsx('settings-menu', {
    'settings-menu-selected': settingsMenuOpen
  });

  return (
    <Box className="AppHeader-Container">
      <Stack
        className="fluid-container"
        direction="row"
        justifyContent="space-between"
        height="100%"
        alignItems="center"
      >
        <Box className="AppHeader-LogoContainer">
          {pointOGmsBaseUrl ? (
            <a href={pointOGmsBaseUrl}>
              <Icon
                className="AppHeader-Logo"
                icon={ICONS.IDONATE_LOGO}
                viewBox="0 0 85 24"
              />
            </a>
          ) : (
            <Link to="/">
              <Icon
                className="AppHeader-Logo"
                icon={ICONS.IDONATE_LOGO}
                viewBox="0 0 85 24"
              />
            </Link>
          )}
        </Box>
        <Tabs
          classes={{
            root: 'AppHeader-Tabs',
            scroller: 'AppHeader-TabsScroller'
          }}
          value={
            // set to false if it's not any of the four top tabs
            // see more: https://mui.com/api/tabs/#props - go to the `value` prop
            navTabs.map((tab) => tab.value).includes(currentPath)
              ? currentPath
              : isReportingTab(currentPath)
          }
        >
          {navTabs.map(({ isFeatureEnabled, ...tab }) => (
            <Tab
              className="AppHeader-NavTab"
              key={tab.label}
              component={NavLinkWithChip(
                isFeatureEnabled,
                tab.value === '/strategy' || tab.value === '/dashboard'
                  ? undefined
                  : pointOGmsBaseUrl
              )}
              {...tab}
            />
          ))}
        </Tabs>
        <div className="AppHeader-Menu">
          <IconButton
            className={settingsButtonClass}
            icon={ICONS.SETTINGS}
            label="settings"
            onClick={handleSettingsMenuClick}
          />
          {user && (
            <OrgSelector orgs={user?.organizations as IOrganization[]} />
          )}
          <Stack
            className="AppHeader-UserMenu"
            direction="row"
            onClick={handleUserMenuClick}
          >
            <Text className="AppHeader-DropdownText" variant="h8">
              {dropdownLabel}
            </Text>
            <Icon className={menuIconClass} icon={ICONS.TRIANGLE} />
          </Stack>
        </div>
        <Menu
          menuItems={profileMenu}
          anchorEl={userMenuAnchorEl}
          open={userMenuOpen}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        />
        <Menu
          menuItems={settingsMenu}
          anchorEl={settingsMenuAnchorEl}
          open={settingsMenuOpen}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        />
      </Stack>
    </Box>
  );
};
